import { useState } from 'react';
import { Formik, Form, Field as FormikField } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { TextField } from 'formik-mui';
import FormHelperText from '@mui/material/FormHelperText';

import { Button } from '../shared';
import useResponsive from '../../../utils/responsive';
import { contactUsDomain } from '../../domain'

const leadGenValidationSchema = Yup.object({
    firstName: Yup.string('').required('Customer name is required'),
    lastName: Yup.string('').required('Customer surname is required'),
    email: Yup.string('').email('Enter a valid email').required('Email is required'),
    phoneNumber: Yup.string('')
        .matches(/^[0-9,+]*$/, 'Phone number is invalid')
        .min(10, 'Phone number is too short (minimum 10)')
        .max(15, 'Phone number is too long (maximum 15)')
        .required('Phone number is required'),
});

const LeadGenRequestCallBackForm = (props) => {
    const { buildingName, roomTypeDescription } = props;
    const [contactUsSubmitError, setContactUsSubmitError] = useState(null);
    const [successfullySubmitted, setSuccessfullySubmitted] = useState(false);
    const { isMobile, isTablet } = useResponsive();
    const leadGenInitialValues = {
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        building: buildingName,
        roomType: roomTypeDescription,
    };

    const onSubmit = async ({ firstName, lastName, email, phoneNumber, building, roomType }) => {
        try {
            await contactUsDomain.submitLeadFromRequestCallBack(
                firstName,
                lastName,
                phoneNumber,
                email,
                building,
                roomType,
            );
            setContactUsSubmitError(null);
            setSuccessfullySubmitted(true);
        } catch (error) {
            setContactUsSubmitError(error.message);
            setSuccessfullySubmitted(false);
        }
    };

    const ThankYouMessage = () => {
        if (!successfullySubmitted) {
            return null;
        }

        return (
            <>
                <div className='lead-gen-request-call-back-thank-you-message-container'>
                    <h3>Thanks for Getting in Touch!</h3>
                    <p>Customer information captured.</p>
                </div>
            </>
        );
    };

    const renderDesktopForm = () => {
        if (successfullySubmitted) {
            return null;
        }
        return (
            <div className='lead-gen-request-call-back-form-container'>
                <Formik
                    initialValues={leadGenInitialValues}
                    validationSchema={leadGenValidationSchema}
                    onSubmit={onSubmit}
                    validateOnBlur
                    validateOnChange={false}
                >
                    {({ submitForm, isSubmitting, touched, errors, handleChange, handleBlur }) => (
                        <Form className='lead-gen-request-call-back-form'>
                            <FormikField className="lead-gen-request-call-back-field"
                                component={TextField}
                                required
                                name="firstName"
                                id="firstName"
                                type="text"
                                label="Customer Name"
                                variant="outlined"
                                color="secondary"
                                helperText={touched.firstName ? errors.firstName : ''}
                                error={touched.firstName && !!errors.firstName}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                fullWidth
                            />

                            <FormikField className="lead-gen-request-call-back-field"
                                component={TextField}
                                required
                                name="lastName"
                                id="lastName"
                                type="text"
                                label="Customer Surname"
                                variant="outlined"
                                color="secondary"
                                helperText={touched.lastName ? errors.lastName : ''}
                                error={touched.lastName && !!errors.lastName}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                fullWidth
                            />

                            <FormikField className="lead-gen-request-call-back-field"
                                component={TextField}
                                required
                                name="phoneNumber"
                                id="phoneNumber"
                                type="tel"
                                label="Contact Number"
                                variant="outlined"
                                color="secondary"
                                helperText={touched.phoneNumber ? errors.phoneNumber : ''}
                                error={touched.phoneNumber && !!errors.phoneNumber}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                fullWidth
                            />

                            <FormikField className="lead-gen-request-call-back-field"
                                component={TextField}
                                required
                                name="email"
                                id="email"
                                type="email"
                                label="Email Address"
                                variant="outlined"
                                color="secondary"
                                helperText={touched.email ? errors.email : ''}
                                error={touched.email && !!errors.email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                fullWidth
                            />

                            <FormikField className="lead-gen-request-call-back-field"
                                component={TextField}
                                name="building"
                                id="building"
                                type="text"
                                label="Building"
                                variant="outlined"
                                color="secondary"
                                fullWidth
                                value={buildingName}
                                hidden={true}
                            />

                            <FormikField className="lead-gen-request-call-back-field"
                                component={TextField}
                                name="roomType"
                                id="roomType"
                                type="text"
                                label="Room Type"
                                variant="outlined"
                                color="secondary"
                                fullWidth
                                value={roomTypeDescription}
                                hidden={true}
                            />

                            <FormHelperText error>{contactUsSubmitError}</FormHelperText>
                            <div className='lead-gen-request-call-back-button-container'>
                                <Button
                                    id="lead-gen-request-call-back-submit-button"
                                    className="lead-gen-request-call-back-button"
                                    variant="contained"
                                    color="primary"
                                    isLoading={isSubmitting}
                                    onClick={submitForm}
                                >
                                    Submit
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        );
    };

    return (
        <>
            {!successfullySubmitted && (
                <div className='lead-gen-request-call-back-form-card'>
                    <div className='lead-gen-request-call-back-form-header'>
                        <h3>Time to Connect</h3>
                        <p className='lead-gen-request-call-back-paragraph'>
                            Your best life is a few clicks away!
                        </p>
                    </div>
                    {renderDesktopForm()}
                </div>
            )}
            {successfullySubmitted && (
                <div className='lead-gen-request-call-back-message-card'>
                    <ThankYouMessage />
                </div>
            )}
        </>
    );
};

export default LeadGenRequestCallBackForm;

LeadGenRequestCallBackForm.propTypes = {
    buildingName: PropTypes.string,
    roomTypeDescription: PropTypes.string,
};

